import { MAX_OTHER_INCOMES_AMOUNT, SecLoanOtherIncomeType } from '@shared/sec-loans'
import { YesNo } from '@ui-common/types'
import { buildOptionList } from '@ui-common/utils'
import { AddItemButton } from '@ui-components/actions/special/AddItemButton'
import { Divider } from '@ui-components/decorative/Divider'
import { FormRadioGroup } from '@ui-components/form/FormRadioGroup'
import { FormRemovableSectionHeader } from '@ui-components/form/FormRemoveableSectionHeader'
import { FormTextInput } from '@ui-components/form/FormTextInput'
import { Fragment } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useToggleableDynamicFormField } from '../../../hooks/useToggleableDynamicFormField'
import { getIfpApplicantInfoLocalizedEnums } from './applicantInfoStepUtils'
import { ApplicantInfoFormData } from './types'

export const otherIncomeFormTestId = 'other-income-form'

export const OtherIncomeFormPart = () => {
  const { formatMessage } = useIntl()
  const { yesNoTexts } = getIfpApplicantInfoLocalizedEnums(formatMessage)

  const { register, control, watch } = useFormContext<ApplicantInfoFormData>()
  const { errors, defaultValues } = useFormState({ name: ['hasOtherIncome'], control })

  const {
    renderFields,
    append,
    remove,
    has: hasOtherIncome,
  } = useToggleableDynamicFormField(
    {
      hasProp: 'hasOtherIncome',
      dataProp: 'otherIncomes',
    },
    control,
    watch,
    register,
    defaultValues?.otherIncomes?.length ?? 0,
    undefined,
    true,
  )

  return (
    <FormRadioGroup
      label={formatMessage({ id: 'sec-loans-income-info-has-other-income-form-label' })}
      description={formatMessage({ id: 'sec-loans-income-info-has-other-income-form-description' })}
      required={true}
      options={buildOptionList(Object.values(YesNo), yesNoTexts)}
      error={errors?.hasOtherIncome?.message}
      data-testid={otherIncomeFormTestId}
      {...register('hasOtherIncome')}
    >
      {hasOtherIncome === YesNo.Yes && (
        <>
          {renderFields.map((child, index) => (
            <Fragment key={child.id}>
              <IncomeInfoStepOtherIncomeForm
                index={index}
                onClick={(e) => remove(e, index)}
                dynamicArrayLength={renderFields.length}
              />
            </Fragment>
          ))}
          <Divider />
          {renderFields.length < MAX_OTHER_INCOMES_AMOUNT ? (
            <AddItemButton
              description={formatMessage({ id: 'sec-loans-income-info-add-other-income-description' })}
              label={formatMessage({ id: 'sec-loans-add-new-child-label' })}
              onClick={append}
            />
          ) : null}
        </>
      )}
    </FormRadioGroup>
  )
}

const IncomeInfoStepOtherIncomeForm = ({
  index,
  onClick,
  dynamicArrayLength,
}: {
  index: number
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  dynamicArrayLength: number
}) => {
  const { formatMessage } = useIntl()
  const { incomeInfoOtherIncomeLabelText } = getIfpApplicantInfoLocalizedEnums(formatMessage)

  const { register, control } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({ name: ['otherIncomes'], control })

  return (
    <>
      <FormRemovableSectionHeader
        index={index}
        onClick={onClick}
        heading={formatMessage({
          id: 'sec-loans-additional-child-info-label',
        })}
        testID={'IncomeInfoStepOtherIncomeForm'}
        dynamicArrayLength={dynamicArrayLength}
        removeActionLabel={formatMessage({ id: 'common-remove' })}
      />
      <FormRadioGroup
        label={formatMessage({ id: 'ifp-loans-applicant-info-other-income-type-label' })}
        required={true}
        options={buildOptionList(Object.values(SecLoanOtherIncomeType), incomeInfoOtherIncomeLabelText)}
        error={errors.otherIncomes?.[index]?.incomeType?.message}
        {...register(`otherIncomes.${index}.incomeType`)}
      />
      <FormTextInput
        required={true}
        inputMode="decimal"
        label={formatMessage({ id: 'ifp-loans-applicant-info-income-after-taxes-label' })}
        error={errors?.otherIncomes?.[index]?.amountAfterTaxes?.message}
        {...register(`otherIncomes.${index}.amountAfterTaxes`)}
      />
    </>
  )
}
